import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { uploadData } from 'aws-amplify/storage';
import { post } from 'aws-amplify/api';

const Camera: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'image' | 'text'>('image');
  const [textInput, setTextInput] = useState<string>('');
  const navigate = useNavigate();

  // Initialize camera when component mounts
  useEffect(() => {
    if (!cameraActive) return;
    
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }, // Use back camera if available
          audio: false
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Failed to access your camera. Please make sure you have granted camera permissions.');
        setCameraActive(false);
      }
    };

    startCamera();

    // Store ref value to use in cleanup
    const currentVideo = videoRef.current;

    // Cleanup when component unmounts or camera is turned off
    return () => {
      const stream = currentVideo?.srcObject as MediaStream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [cameraActive]);

  const handleStartCamera = () => {
    setCameraActive(true);
    setCapturedImage(null);
    setError(null);
  };

  const handleCaptureImage = () => {
    console.log("handleCaptureImage");
    console.log("videoRef.current", videoRef.current);
    console.log("canvasRef.current", canvasRef.current);
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match the video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const context = canvas.getContext('2d');

    console.log("context", context);
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to data URL
      const imageDataURL = canvas.toDataURL('image/jpeg');
      setCapturedImage(imageDataURL);
      
      // Stop camera stream
      const stream = video.srcObject as MediaStream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }
      setCameraActive(false);
    }
  };

  const handleAnalyzeImage = async () => {
    if (!capturedImage) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current authenticated user
      const user = await getCurrentUser();
      const userId = user.userId;
      
      // Convert data URL to blob
      const blob = await fetch(capturedImage).then(res => res.blob());
      
      // Create unique file name with user ID and timestamp
      const fileName = `${userId}/${Date.now()}.jpg`;
      
      const session = (await fetchAuthSession()) ?? {};
      console.log('Session:', session);
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      
      // Get credentials from Cognito Identity Pool
      await fetchAuthSession(); // Refresh the session

      // Upload to S3
      console.log('Uploading to S3...');
      try {
        await uploadData({
          key: fileName,
          data: blob,
          options: {
            contentType: 'image/jpeg',
            accessLevel: 'private',
            onProgress: ({ transferredBytes, totalBytes }: { transferredBytes: number; totalBytes: number }) => {
              const percentComplete = Math.round((transferredBytes / totalBytes) * 100);
              console.log(`Upload progress: ${percentComplete}%`);
            }
          }
        }).result;
        
        console.log('File uploaded successfully');
       
        console.log('Proceeding with analysis');
      } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
      }

      // Call Lambda function to analyze image
      const storageKey = "private/"+session.identityId+"/"+fileName;

      const restOperation = post({
        apiName: 'FoodTrackerAPI',
        path: '/food/analyze',
        options: {
          body: {
            storageKey: storageKey,
            filename: fileName
          },
          headers: {
            Authorization: authToken
          }
        }
      });

      const { body } = await restOperation.response;
      const responseData = await body.json() as { mealData: { mealTimestamp: string } };
      
      console.log('Analysis response:', responseData);
      if (responseData?.mealData) {
        navigate(`/analysis`, { state: { mealData: responseData.mealData } });
      } else {
        setError('Failed to analyze the image. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setError('Failed to process the image. Please try again.');
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCameraActive(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Create a FileReader to read the image
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setCapturedImage(dataUrl);
      setCameraActive(false);
    };
    reader.onerror = () => {
      setError('Failed to read the selected image');
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleAnalyzeText = async () => {
    if (!textInput.trim()) {
      setError('Please enter a food description');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const restOperation = post({
        apiName: 'FoodTrackerAPI',
        path: '/food/analyze-text',
        options: {
          body: {
            title: "Food Description",
            content: textInput
          },
          headers: {
            Authorization: authToken
          }
        }
      });

      const { body } = await restOperation.response;
      const responseData = await body.json() as { mealData: { mealId: string } };

      if (responseData?.mealData) {
        navigate(`/analysis`, { state: { mealData: responseData.mealData } });
      } else {
        setError('Failed to analyze the text. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error analyzing text:', error);
      setError('Failed to analyze the text. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ height: '100%', maxHeight: '100vh', overflowY: 'hidden', margin: 0, display: 'flex', flexDirection: 'column' }}>
      {!cameraActive && (
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'image' ? 'active' : ''}`}
            onClick={() => setActiveTab('image')}
          >
            Image
          </button>
          <button 
            className={`tab ${activeTab === 'text' ? 'active' : ''}`}
            onClick={() => setActiveTab('text')}
          >
            Text
          </button>
        </div>
      )}
      
      {error && <div className="error-message">{error}</div>}

      {activeTab === 'text' ? (
        <div className="text-input-container">
          <p style={{ color: 'var(--logo-grey)', marginBottom: '0.5rem' }}>
            List all the items in your meal with their quantities for the most accurate analysis.
          </p>
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Example: 2 scrambled eggs, 1 slice whole wheat toast with butter, 1 medium apple, 1 cup black coffee"
            rows={5}
            className="text-input"
            aria-label="Food description input"
          />
          <button
            onClick={handleAnalyzeText}
            className="btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                <span style={{ marginLeft: '10px' }}>Analyzing...</span>
              </>
            ) : 'Analyze Text'}
          </button>
          <div style={{ fontSize: '0.9rem', color: 'var(--logo-grey)', marginTop: '0.5rem' }}>
            <h4 style={{ color: 'var(--logo-black)', marginBottom: '0.5rem' }}>Tips for best results:</h4>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li>Include quantities for each item (e.g., 2 slices, 1 cup)</li>
              <li>Specify cooking methods when relevant (e.g., grilled, baked)</li>
              <li>Mention any toppings or condiments used</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="camera-container" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {cameraActive ? (
          <>
            <div style={{ flex: 1, position: 'relative' }}>
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                className="camera-view"
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <button 
              onClick={handleCaptureImage} 
              className="btn"
              style={{ 
                margin: '16px',
                height: '48px',
                fontSize: '16px'
              }}
            >
              Take Photo
            </button>
          </>
        ) : capturedImage ? (
          <>
            <img 
              src={capturedImage} 
              alt="Captured food" 
              style={{ display: 'block', width: '100%', borderRadius: '8px' }} 
            />
            <div className="camera-btn-container" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <button 
                onClick={handleRetake} 
                className="btn btn-secondary"
                disabled={loading}
              >
                Retake
              </button>
              <button 
                onClick={handleAnalyzeImage} 
                className="btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner" style={{ width: '20px', height: '20px', marginRight: '10px' }}></span>
                    Analyzing...
                  </>
                ) : 'Analyze Image'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p>Take a photo or select an image of your food to analyze calories</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button onClick={handleStartCamera} className="btn">
                Open Camera
              </button>
              <button onClick={handleUploadClick} className="btn btn-secondary">
                Upload Image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        )}
        </div>
      )}
      
      {activeTab === 'image' && (
        /* Hidden canvas for image processing */        
        <div className="camera-tips" style={{ marginTop: '20px', fontSize: '0.9rem' }}>
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
          <h4>Tips for best results:</h4>
          <ul>
            <li>Make sure all food items are clearly visible</li>
            <li>Take the photo in good lighting</li>
            <li>Include the entire plate in the frame</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default Camera;
